import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import { csrfFetch, clearCsrfToken, getCsrfToken } from '../utils/csrfFetch';
import { buildApiUrl } from '../config';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const accessToken = Cookies.get('accessToken');
    const refreshToken = Cookies.get('refreshToken');
    
    if (storedUser && accessToken) {
      const user = JSON.parse(storedUser);
      user.accessToken = accessToken;
      user.refreshToken = refreshToken;
      setUser(user);
    }
    setLoading(false);
  }, []);

  const login = async (email, password, twoFactorCode = '', twoFactorRecoveryCode = '') => {
    try {
      const response = await fetch(buildApiUrl('/account/login?useSessionCookies=false'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // ensure auth cookies from server are set
        body: JSON.stringify({ email, password, twoFactorCode, twoFactorRecoveryCode }),
      });
      
      let data;
      const contentType = response.headers.get('content-type');
      
      const text = await response.text();
      
      if (text.trim()) {
        try {
          data = JSON.parse(text);
        } catch (parseError) {

          throw new Error(`Invalid JSON response: ${text}`);
        }
      } else {
        // Handle empty response - this might be expected for useCookies=true
        data = {
          tokenType: "string",
          accessToken: "string", 
          expiresIn: 0,
          refreshToken: "string"
        };
      }
      
      if (!response.ok) {
        // Check if backend indicates 2FA is required
        if (data?.title === 'RequiresTwoFactor') {
          return { success: false, requires2fa: true, error: data?.message || 'Two-factor required' };
        }
        if (data?.detail && data?.errors) {
          return { success: false, error: data?.errors, detail: data.detail };
        }
        throw new Error(data.message || `Login failed with status: ${response.status}`);
      }
      
      // Store tokens in secure cookies
      const cookieOptions = {
        expires: 7, // 7 days
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      };
      
      Cookies.set('accessToken', data.accessToken, cookieOptions);
      Cookies.set('refreshToken', data.refreshToken, { ...cookieOptions, expires: 30 }); // Refresh token expires in 30 days

      // Create user object without sensitive data
      const user = {
        id: data.id || data.userId,
        email: data.email || email,
        name: data.name || email.split('@')[0],
        role: 'user',
        firstLogin: data.firstLogin || false,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        tokenType: data.tokenType || 'Bearer',
        expiresIn: data.expiresIn
      };
      
      localStorage.setItem('user', JSON.stringify({
        id: user.id,
        email: user.email,
        name: user.name,
        role: user.role,
        firstLogin: user.firstLogin
      }));
      
      setUser(user);
      
      // Warm CSRF token cache for subsequent POST requests
      try {
        await getCsrfToken();
      } catch (err) {
      }
      
      navigate('/');
      
      return { success: true };
    } catch (error) {

      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Attempt server-side logout to clear auth session cookie
      const resp = await csrfFetch('/account/logout', {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      if (!resp.ok) {
        console.warn('Server logout failed, proceeding with client cleanup');
      }
    } catch (err) {
      toast.error('Logout request errored, proceeding with client cleanup', err);
    } finally {
      localStorage.removeItem('user');
      Cookies.remove('accessToken');
      Cookies.remove('refreshToken');
      clearCsrfToken();
      setUser(null);
      navigate('/login');
    }
  };

  const refreshToken = async () => {
    try {
      const storedRefreshToken = Cookies.get('refreshToken');
      if (!storedRefreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await csrfFetch('/account/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: storedRefreshToken }),
      });

      if (!response.ok) {
        throw new Error('Token refresh failed');
      }

      const data = await response.json();
      
      // Update cookies with new tokens
      const cookieOptions = {
        expires: 7,
        secure: process.env.NODE_ENV === 'production',
        sameSite: 'strict'
      };
      
      Cookies.set('accessToken', data.accessToken, cookieOptions);
      if (data.refreshToken) {
        Cookies.set('refreshToken', data.refreshToken, { ...cookieOptions, expires: 30 });
      }

      // Update user with new tokens
      const updatedUser = {
        ...user,
        accessToken: data.accessToken,
        refreshToken: data.refreshToken || storedRefreshToken,
        expiresIn: data.expiresIn
      };
      
      setUser(updatedUser);
      return { success: true, accessToken: data.accessToken };
    } catch (error) {
      logout(); // Force logout on refresh failure
      return { success: false, error: error.message };
    }
  };


  return (
    <AuthContext.Provider value={{
      user,
      login,
      logout,
      refreshToken,
      loading,
      isAuthenticated: !!user,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
